import { useDeleteTestData } from "@/api/admin/useDeleteTestData";
import { useGenerateTestUsers } from "@/api/admin/useGenerateTestUsers";
import { Alert, Button, Group, Modal, NumberInput, Paper, Stack, Text } from "@mantine/core";
import { useState } from "react";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "./utils/errorNotifications";

const DEFAULT_TEST_USER_COUNT = 30;
const MAX_TEST_USER_COUNT = 100;

type ConfirmAction = "GENERATE" | "DELETE";

export const TestDataPanel = () => {
  const generateMutation = useGenerateTestUsers();
  const deleteMutation = useDeleteTestData();
  const isPending = generateMutation.isPending || deleteMutation.isPending;
  const [count, setCount] = useState<number>(DEFAULT_TEST_USER_COUNT);
  const [pendingConfirmation, setPendingConfirmation] = useState<ConfirmAction | null>(null);

  const handleGenerate = async () => {
    try {
      const result = await generateMutation.mutateAsync({ count });
      showSuccessNotification(
        "테스트 유저 생성 완료",
        `테스트 유저 ${result.createdUserCount}명, 예약 요청 ${result.createdReservationRequestCount}개를 생성했습니다.`
      );
      if (result.failedUserCount > 0) {
        showWarningNotification(
          "일부 생성 실패",
          `테스트 유저 ${result.failedUserCount}명은 생성하지 못했습니다. 다시 시도해주세요.`
        );
      }
    } catch (error) {
      showErrorNotification(error, "테스트 유저 생성 실패");
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteMutation.mutateAsync();
      showSuccessNotification(
        "테스트 데이터 삭제 완료",
        `테스트 유저 ${result.deletedUserCount}명과 테스트 전용 이벤트 ${result.deletedEventCount}개를 삭제했습니다.`
      );
      if (result.failedAuthUserDeletionCount > 0) {
        showWarningNotification(
          "일부 로그인 계정 삭제 실패",
          `로그인 계정 ${result.failedAuthUserDeletionCount}개를 삭제하지 못했습니다. 앱 데이터는 모두 삭제되어 사용되지 않습니다.`
        );
      }
    } catch (error) {
      showErrorNotification(error, "테스트 데이터 삭제 실패");
    }
  };

  const handleConfirm = () => {
    const action = pendingConfirmation;
    setPendingConfirmation(null);

    switch (action) {
      case "GENERATE":
        handleGenerate();
        break;
      case "DELETE":
        handleDelete();
        break;
      default:
        break;
    }
  };

  return (
    <Paper withBorder p="sm" radius="md" bg="grape.0">
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Text size="sm" fw={700} c="black">
          테스트 데이터
        </Text>
        <Group gap="xs" wrap="nowrap">
          <NumberInput
            size="xs"
            w={70}
            min={1}
            max={MAX_TEST_USER_COUNT}
            value={count}
            onChange={(value) =>
              setCount(typeof value === "number" ? value : DEFAULT_TEST_USER_COUNT)
            }
            disabled={isPending}
            aria-label="생성할 테스트 유저 수"
          />
          <Button
            size="xs"
            color="grape"
            loading={generateMutation.isPending}
            disabled={isPending || count < 1 || count > MAX_TEST_USER_COUNT}
            onClick={() => setPendingConfirmation("GENERATE")}
          >
            유저 생성
          </Button>
          <Button
            size="xs"
            variant="outline"
            color="red"
            loading={deleteMutation.isPending}
            disabled={isPending}
            onClick={() => setPendingConfirmation("DELETE")}
          >
            전체 삭제
          </Button>
        </Group>
      </Group>

      <Modal
        opened={pendingConfirmation !== null}
        onClose={() => setPendingConfirmation(null)}
        title={
          <Text fw={700} c="black">
            {pendingConfirmation === "GENERATE"
              ? `테스트 유저 ${count}명을 생성할까요?`
              : "모든 테스트 데이터를 삭제할까요?"}
          </Text>
        }
        centered
        styles={{ body: { color: "black" } }}
      >
        <Stack gap="md">
          {pendingConfirmation === "GENERATE" && (
            <Alert color="grape" variant="light">
              <Stack gap="xs">
                <Text size="sm">
                  이름 앞에 [TEST]가 붙은 테스트 유저를 만들고, 각 유저마다 성격 테스트 답변과 예약
                  가능한 날짜의 예약 요청 2~3개를 생성합니다.
                </Text>
                <Text size="sm">
                  테스트 유저는 전화번호가 없어 문자를 받지 않습니다. 실제 유저와 같은 대기 목록에
                  표시되니, 실제 이벤트에 섞이지 않도록 주의해주세요.
                </Text>
              </Stack>
            </Alert>
          )}
          {pendingConfirmation === "DELETE" && (
            <Alert color="red" variant="light">
              <Text size="sm">
                모든 테스트 유저와 그들의 예약 요청, 성격 테스트, 피드백, 평가를 삭제합니다. 테스트
                유저만 참석한 이벤트도 함께 삭제되며, 실제 참석자가 있는 이벤트는 유지됩니다. 이
                작업은 되돌릴 수 없습니다.
              </Text>
            </Alert>
          )}

          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setPendingConfirmation(null)}>
              취소
            </Button>
            <Button
              color={pendingConfirmation === "DELETE" ? "red" : "grape"}
              onClick={handleConfirm}
            >
              {pendingConfirmation === "DELETE" ? "전체 삭제" : "생성"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
};
